import { useId, useState } from 'react';
import type { DocumentStatementBasis, StatementBasis } from '../api/types';
import {
  STATEMENT_BASIS_LABELS,
  STATEMENT_BASIS_OPTIONS,
  statementBasisConfidenceNote,
  statementBasisText,
} from '../lib/statement-basis';
import { Button } from './button';

/**
 * A financial statement's accounting basis as one plain line —
 * "Basis: Accrual (from the document)" (ADR-0053). `showConfidence` adds the
 * AI's confidence for the admin. Renders nothing when there is no basis.
 */
export function StatementBasisLine({
  value,
  showConfidence = false,
  className,
}: {
  value: DocumentStatementBasis | null | undefined;
  showConfidence?: boolean;
  className?: string;
}) {
  if (!value) return null;
  const note = showConfidence ? statementBasisConfidenceNote(value) : null;
  return (
    <p className={className}>
      Basis: {statementBasisText(value)}
      {note ? <span className="text-muted-foreground"> · {note}</span> : null}
    </p>
  );
}

/**
 * The basis with a "Correct it" control for the two people who may (the room
 * admin and the bound seller): a choice of the four bases and Save. The page
 * owns the request; `onSave` runs it (calling `onSaved` once it lands, which
 * closes the control) and `pending` / `error` report it. Every
 * save is recorded as that person's answer (and audited by the server).
 */
export function StatementBasisEditor({
  value,
  showConfidence = false,
  pending = false,
  error = null,
  onSave,
}: {
  value: DocumentStatementBasis | null | undefined;
  showConfidence?: boolean;
  pending?: boolean;
  error?: string | null;
  onSave: (basis: StatementBasis, onSaved: () => void) => void;
}) {
  const selectId = useId();
  const [editing, setEditing] = useState(false);
  const [choice, setChoice] = useState<StatementBasis>(value?.basis ?? 'not_stated');

  return (
    <div className="space-y-2 text-sm">
      {value ? (
        <StatementBasisLine value={value} showConfidence={showConfidence} />
      ) : (
        <p className="text-muted-foreground">Basis: not read from this document.</p>
      )}
      {editing ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <label htmlFor={selectId} className="text-xs font-medium text-muted-foreground">
              Accounting basis
            </label>
            <select
              id={selectId}
              className="flex h-8 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              value={choice}
              onChange={(e) => setChoice(e.target.value as StatementBasis)}
              disabled={pending}
            >
              {STATEMENT_BASIS_OPTIONS.map((b) => (
                <option key={b} value={b}>
                  {STATEMENT_BASIS_LABELS[b]}
                </option>
              ))}
            </select>
          </div>
          <Button
            size="sm"
            disabled={pending}
            onClick={() => onSave(choice, () => setEditing(false))}
          >
            {pending ? 'Saving…' : 'Save'}
          </Button>
          <Button size="sm" variant="ghost" disabled={pending} onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setChoice(value?.basis ?? 'not_stated');
            setEditing(true);
          }}
        >
          {value ? 'Correct the basis' : 'Set the basis'}
        </Button>
      )}
      {error ? (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
